import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { OrdenesTransporteFilterDto } from '@opb-workspace/shared';

export interface TransportOrdersState {
  filters: OrdenesTransporteFilterDto;
  lastUpdated: number;
}

@Injectable({
  providedIn: 'root'
})
export class TransportOrdersStateService {
  private readonly STATE_KEY = 'transport_orders_state';
  private readonly STATE_EXPIRY_HOURS = 24;

  private readonly defaultState: TransportOrdersState = {
    filters: {
      page: 1,
      limit: 10,
      orderBy: 'etd',
      orderDirection: 'DESC'
    },
    lastUpdated: Date.now()
  };

  private stateSubject = new BehaviorSubject<TransportOrdersState>(this.defaultState);

  constructor() {
    this.initializeState();
  }

  private initializeState(): void {
    const savedState = this.loadStateFromStorage();
    if (savedState && !this.isStateExpired(savedState.lastUpdated)) {
      this.stateSubject.next(savedState);
    }
  }

  private isStateExpired(lastUpdated: number): boolean {
    const now = Date.now();
    const expiryTime = this.STATE_EXPIRY_HOURS * 60 * 60 * 1000;
    return (now - lastUpdated) > expiryTime;
  }

  getState(): Observable<TransportOrdersState> {
    return this.stateSubject.asObservable();
  }

  getCurrentState(): TransportOrdersState {
    return this.stateSubject.value;
  }

  updateState(partialState: Partial<TransportOrdersState>): void {
    const currentState = this.stateSubject.value;
    const newState: TransportOrdersState = {
      ...currentState,
      ...partialState,
      lastUpdated: Date.now()
    };
    this.stateSubject.next(newState);
    this.saveStateToStorage(newState);
  }

  updateFilters(filters: Partial<OrdenesTransporteFilterDto>): void {
    const currentState = this.getCurrentState();
    const isPaginationChange = 'page' in filters || 'limit' in filters;

    this.updateState({
      filters: {
        ...currentState.filters,
        ...filters,
        // Reset to page 1 if any filter other than pagination changes
        page: isPaginationChange ? (filters.page || currentState.filters.page) : 1
      }
    });
  }

  clearState(): void {
    this.stateSubject.next(this.defaultState);
    this.clearStateFromStorage();
  }

  private saveStateToStorage(state: TransportOrdersState): void {
    try {
      localStorage.setItem(this.STATE_KEY, JSON.stringify(state));
    } catch (error) {
      console.warn('Error saving Transport Orders state to localStorage:', error);
    }
  }

  private loadStateFromStorage(): TransportOrdersState | null {
    try {
      const saved = localStorage.getItem(this.STATE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.warn('Error loading Transport Orders state from localStorage:', error);
    }
    return null;
  }

  private clearStateFromStorage(): void {
    try {
      localStorage.removeItem(this.STATE_KEY);
    } catch (error) {
      console.warn('Error clearing Transport Orders state from localStorage:', error);
    }
  }

  /**
   * Convert state to query parameters for URL
   */
  stateToQueryParams(state: TransportOrdersState): any {
    const params: any = {};
    const filters = state.filters;

    if (filters.clienteId) params.clienteId = filters.clienteId.toString();
    if (filters.exportadorId) params.exportadorId = filters.exportadorId.toString();
    if (filters.nave) params.nave = filters.nave;
    if (filters.temporadaId) params.temporadaId = filters.temporadaId.toString();
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (filters.referenciaCliente) params.referenciaCliente = filters.referenciaCliente;
    if (filters.contenedor) params.contenedor = filters.contenedor;
    if (filters.dus) params.dus = filters.dus;
    if (filters.nroLiquidacion) params.nroLiquidacion = filters.nroLiquidacion.toString();
    if (filters.booking) params.booking = filters.booking;
    if (filters.licensePlate) params.licensePlate = filters.licensePlate;

    // Pagination
    if (filters.page && filters.page !== 1) params.page = filters.page.toString();
    if (filters.limit && filters.limit !== 10) params.limit = filters.limit.toString();
    if (filters.orderBy && filters.orderBy !== 'etd') params.orderBy = filters.orderBy;
    if (filters.orderDirection && filters.orderDirection !== 'DESC') params.orderDirection = filters.orderDirection;

    return params;
  }

  /**
   * Convert query parameters from URL to state
   */
  queryParamsToState(params: any): TransportOrdersState {
    return {
      filters: {
        clienteId: params.clienteId ? +params.clienteId : undefined,
        exportadorId: params.exportadorId ? +params.exportadorId : undefined,
        nave: params.nave || undefined,
        temporadaId: params.temporadaId ? +params.temporadaId : undefined,
        dateFrom: params.dateFrom || undefined,
        dateTo: params.dateTo || undefined,
        referenciaCliente: params.referenciaCliente || undefined,
        contenedor: params.contenedor || undefined,
        dus: params.dus || undefined,
        nroLiquidacion: params.nroLiquidacion ? +params.nroLiquidacion : undefined,
        booking: params.booking || undefined,
        licensePlate: params.licensePlate || undefined,
        page: params.page ? +params.page : 1,
        limit: params.limit ? +params.limit : 10,
        orderBy: params.orderBy || 'etd',
        orderDirection: (params.orderDirection as 'ASC' | 'DESC') || 'DESC'
      },
      lastUpdated: Date.now()
    };
  }
}
